/**
 * AgentCardShell — the row every domain agent shows in the Connectors modal and the Domain agents panel.
 *
 * Left: a tinted icon tile, the name and a one-line description. Right: the action — a "Connect" button when it is not
 * connected, a "Connected" pill (with a small unplug button to disconnect) when it is. Anything else the state needs
 * (a "Use in chat" switch, an organization list, a warning) sits below the row.
 */
import React from 'react';
import { Unplug } from 'lucide-react';
import { T2 } from './AppShellV2.tsx';

export type PlugState = 'connected' | 'warning' | 'disconnected' | 'checking';

const TONE = {
  ok:   { dot: 'var(--lyra-color-success)', fg: 'var(--lyra-color-success)', bg: 'var(--lyra-color-success-bg)', ring: 'var(--app-success-border)' },
  warn: { dot: 'var(--lyra-color-warning)', fg: 'var(--lyra-color-warning)', bg: 'var(--lyra-color-warning-bg)', ring: 'var(--app-warning-border)' },
  muted: { dot: 'var(--app-text-3)', fg: 'var(--app-text-2)', bg: 'var(--app-surface-3)', ring: 'var(--lyra-color-border)' },
};

const PILL: Record<PlugState, { tone: keyof typeof TONE; text: string }> = {
  connected: { tone: 'ok', text: 'Connected' },
  warning: { tone: 'warn', text: 'Needs attention' },
  checking: { tone: 'muted', text: 'Checking…' },
  disconnected: { tone: 'muted', text: 'Not connected' },
};

/** The primary call to action on a row that is not connected. */
export function ConnectButton({ children = 'Connect', onClick, disabled, label }: { children?: React.ReactNode; onClick: () => void; disabled?: boolean; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      style={{ flexShrink: 0, padding: '7px 16px', borderRadius: 10, border: 'none', background: T2.accent, color: 'var(--lyra-color-primary-fg, var(--app-on-accent))', fontSize: 12.5, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1, fontFamily: 'inherit', whiteSpace: 'nowrap' }}
    >
      {children}
    </button>
  );
}

/**
 * The connection control in a row's corner. Not connected and clickable: the Connect button. Connected / needs attention: a status
 * pill, plus a small unplug button when clicking can disconnect (its accessible name says what it does, e.g. "Disconnect Workforce").
 */
export function PlugButton({ state, label, onClick, disabled }: {
  state: PlugState;
  /** Accessible name and tooltip, e.g. "Disconnect Workforce". */
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  if (state === 'disconnected') {
    return onClick ? <ConnectButton onClick={onClick} disabled={disabled} label={label}>Connect</ConnectButton> : null;
  }
  const { tone, text } = PILL[state];
  const c = TONE[tone];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
      <span role="img" aria-label={onClick ? text : label} title={onClick ? text : label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 999, border: `1px solid ${c.ring}`, background: c.bg, color: c.fg, fontSize: 12, fontWeight: 600, opacity: state === 'checking' ? 0.7 : 1, whiteSpace: 'nowrap' }}>
        <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: c.dot }} />{text}
      </span>
      {onClick && (
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
          title={label}
          style={{ width: 30, height: 30, borderRadius: 999, border: `1px solid ${T2.border}`, background: 'transparent', color: 'var(--lyra-color-text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}
        >
          <Unplug size={14} />
        </button>
      )}
    </span>
  );
}

export function StatusPill({ tone, children }: { tone: 'ok' | 'warn' | 'muted'; children: React.ReactNode }) {
  const c = TONE[tone];
  return (
    <span role="status" style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 6, padding: '4px 10px', borderRadius: 12, background: c.bg, color: c.fg, fontSize: 11.5, fontWeight: 600, maxWidth: '100%' }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: c.dot, flexShrink: 0, marginTop: 5 }} />
      <span style={{ lineHeight: 1.35, overflowWrap: 'anywhere' }}>{children}</span>
    </span>
  );
}

/** "Use in chat" as a switch: on = the chat sends its queries to this agent. */
export function UseInChatSwitch({ on, onToggle, disabled, hint }: { on: boolean; onToggle: () => void; disabled?: boolean; hint?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label="Use in chat"
      title={disabled ? hint : undefined}
      disabled={disabled}
      onClick={onToggle}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '7px 10px', border: `1px solid ${on ? T2.accent : T2.border}`, borderRadius: 10, background: on ? T2.accentSoft : 'transparent', color: T2.ink, fontSize: 12.5, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1, fontFamily: 'inherit' }}
    >
      <span>{on ? 'In chat' : 'Use in chat'}</span>
      <span aria-hidden style={{ width: 32, height: 18, borderRadius: 999, background: on ? T2.accent : 'var(--app-border-strong)', position: 'relative', transition: 'background .15s ease' }}>
        <span style={{ position: 'absolute', top: 2, left: on ? 16 : 2, width: 14, height: 14, borderRadius: 999, background: 'var(--lyra-color-surface)', transition: 'left .15s ease', boxShadow: '0 1px 2px rgba(0,0,0,0.25)' }} />
      </span>
    </button>
  );
}

export default function AgentCardShell({ Icon, bg, fg, name, subtitle, plug, selected = false, children }: {
  Icon: React.ComponentType<{ size?: number }>;
  bg: string;
  fg: string;
  name: string;
  subtitle: string;
  plug: React.ReactNode;
  selected?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div
      data-testid="agent-card"
      style={{
        borderRadius: 14, padding: '12px 14px', minWidth: 0,
        border: `1px solid ${selected ? T2.accent : T2.border}`,
        background: selected ? T2.accentSoft : 'var(--lyra-color-surface-2)',
        transition: 'border-color .15s ease, background .15s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={19} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: T2.ink, letterSpacing: -0.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
          <div style={{ fontSize: 12, color: T2.ink3, lineHeight: 1.4 }}>{subtitle}</div>
        </div>
        {plug}
      </div>
      {children && <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 11 }}>{children}</div>}
    </div>
  );
}
